import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { type FilterType, type Tab, FILTER_TYPES } from './types'

const GLOBAL_ID_FILTERS: FilterType[] = ['companyFields', 'companyIndustries']
const isDatabaseId = (value: string) => /^\d+$/.test(value)

export function useVisitorProfilesRouter(defaultPerPage: number) {
  const route = useRoute()
  const router = useRouter()

  const selectedTab = computed(() => ((route.params.tabId as string) || 'all') as Tab)
  const currentPage = computed(() => parseInt((route.query.page as string) || '1', 10))
  const perPage = computed(() => parseInt((route.query.perPage as string) || defaultPerPage.toString(), 10))

  const selectedFilters = computed(() => {
    const filters = FILTER_TYPES.reduce(
      (acc, type) => {
        const filter = (route.query[type] as string | string[]) ?? []
        const values = Array.isArray(filter) ? filter : [filter]
        acc[type] = GLOBAL_ID_FILTERS.includes(type) ? values.filter((value) => !isDatabaseId(value)) : values
        return acc
      },
      {} as Record<FilterType, string[]>,
    )

    return filters
  })

  function handleTabUpdate(tabId: string) {
    if (tabId === 'all') {
      router.push({ name: 'visitorProfilesList' })
    } else {
      router.push({ name: 'visitorProfilesListTab', params: { tabId } })
    }
  }

  function updateCurrentPage(newPage: number) {
    router.push({
      query: {
        ...route.query,
        page: newPage.toString(),
      },
    })
    window?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function updatePerPage(newPerPage: number | 'all') {
    router.push({
      query: {
        ...route.query,
        page: '1',
        perPage: newPerPage.toString(),
      },
    })
    window?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function updateFilters(type: FilterType, filters: string[]) {
    router.push({
      query: {
        ...route.query,
        page: '1',
        [type]: filters.length ? filters : undefined,
      },
    })
  }

  function resetFilters() {
    router.push({
      query: {
        page: '1',
        perPage: perPage.value.toString(),
      },
    })
  }

  return {
    selectedTab,
    currentPage,
    perPage,
    selectedFilters,
    handleTabUpdate,
    updateCurrentPage,
    updatePerPage,
    updateFilters,
    resetFilters,
  }
}
